/* Darling Luxury — DLX mega menu (desktop) + menu drawer (mobile) */
(() => {
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('dlx-mega-menu')) {
    class DlxMegaMenu extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.openItem = null;
        this.timer = null;
        this.items = [...this.querySelectorAll('.dlx-mm__item.has-panel')];
        this.tops = [...this.querySelectorAll('.dlx-mm__top')];

        this.items.forEach((li) => {
          li.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse') return;
            this.schedule(() => this.open(li), this.openItem ? 60 : 150);
          });
          li.addEventListener('pointerleave', (e) => {
            if (e.pointerType !== 'mouse') return;
            this.schedule(() => this.close(), 250);
          });
          const btn = li.querySelector('.dlx-mm__toggle');
          btn?.addEventListener('click', () => {
            if (this.openItem === li) this.close();
            else this.open(li);
          });
          btn?.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              this.open(li);
              li.querySelector('.dlx-mm__panel a')?.focus();
            }
          });
          li.querySelectorAll('[data-cat]').forEach((a) => {
            const act = () => this.setCat(li, a.dataset.cat);
            a.addEventListener('pointerenter', act);
            a.addEventListener('focus', act);
          });
        });

        this.tops.forEach((a, i) => {
          a.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const n = this.tops[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tops.length) % this.tops.length];
              n?.focus();
            }
          });
        });

        this.onKey = (e) => {
          if (e.key === 'Escape' && this.openItem) {
            const btn = this.openItem.querySelector('.dlx-mm__toggle');
            this.close();
            btn?.focus();
          }
        };
        this.onFocusOut = (e) => {
          if (this.openItem && !this.openItem.contains(e.relatedTarget)) this.close();
        };
        this.onDocClick = (e) => {
          if (this.openItem && !this.contains(e.target)) this.close();
        };
        this.onReflow = () => {
          if (this.openItem) this.position();
        };
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('click', this.onDocClick);
        this.addEventListener('focusout', this.onFocusOut);
        window.addEventListener('scroll', this.onReflow, { passive: true });
        window.addEventListener('resize', this.onReflow);
      }

      disconnectedCallback() {
        this._ready = false;
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('click', this.onDocClick);
        window.removeEventListener('scroll', this.onReflow);
        window.removeEventListener('resize', this.onReflow);
      }

      schedule(fn, ms) {
        clearTimeout(this.timer);
        this.timer = setTimeout(fn, ms);
      }

      position() {
        const header = this.closest('header-component, .header, header') || this;
        const bar = this.getBoundingClientRect();
        const hb = header.getBoundingClientRect();
        const top = Math.max(bar.bottom, Math.min(hb.bottom, bar.bottom + 24));
        this.style.setProperty('--dlx-menu-top', `${Math.round(top)}px`);
        document.documentElement.style.setProperty('--dlx-menu-top', `${Math.round(top)}px`);
      }

      open(li) {
        clearTimeout(this.timer);
        if (this.openItem === li) return;
        if (this.openItem) this.close(true);
        this.position();
        const simple = li.querySelector('.dlx-mm__panel--simple');
        if (simple) {
          const r = li.getBoundingClientRect();
          const w = simple.offsetWidth;
          const left = Math.max(24, Math.min(r.left - 8, window.innerWidth - w - 24));
          simple.style.setProperty('--dlx-panel-left', `${Math.round(left)}px`);
        }
        this.openItem = li;
        li.classList.add('is-open');
        li.querySelector('.dlx-mm__toggle')?.setAttribute('aria-expanded', 'true');
      }

      close() {
        clearTimeout(this.timer);
        const li = this.openItem;
        if (!li) return;
        li.classList.remove('is-open');
        li.querySelector('.dlx-mm__toggle')?.setAttribute('aria-expanded', 'false');
        this.openItem = null;
      }

      setCat(li, n) {
        li.querySelectorAll('[data-cat]').forEach((el) => el.classList.toggle('is-current', el.dataset.cat === n));
        li.querySelectorAll('[data-cat-panel]').forEach((el) => el.classList.toggle('is-current', el.dataset.catPanel === n));
      }
    }
    customElements.define('dlx-mega-menu', DlxMegaMenu);
  }

  if (!customElements.get('dlx-menu-drawer')) {
    class DlxMenuDrawer extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.burger = this.querySelector('[data-dlx-open]');
        this.panel = this.panel || this.querySelector('.dlx-md__panel');
        this.overlay = this.overlay || this.querySelector('.dlx-md__overlay');
        if (!this.panel || !this.overlay) return;

        // Portal to <body> so the fixed panel is never clipped by the sticky header.
        document.body.append(this.overlay, this.panel);

        this.burger?.addEventListener('click', () => this.open());
        this.overlay.addEventListener('click', () => this.close());
        this.panel.querySelectorAll('[data-dlx-close]').forEach((b) => b.addEventListener('click', () => this.close()));
        this.panel.querySelectorAll('[data-open-view]').forEach((b) =>
          b.addEventListener('click', () => this.showView(b.dataset.openView, b))
        );
        this.panel.querySelectorAll('[data-back]').forEach((b) => b.addEventListener('click', () => this.showView('root')));

        this.onKey = (e) => {
          if (this.panel.hidden) return;
          if (e.key === 'Escape') this.close();
          if (e.key === 'Tab') this.trap(e);
        };
        this.onCart = (e) => {
          const count = e?.detail?.resource?.item_count ?? e?.detail?.data?.itemCount;
          if (typeof count === 'number') {
            const el = this.panel.querySelector('[data-dlx-count]');
            if (el) {
              el.textContent = count;
              el.hidden = count === 0;
            }
          }
        };
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('cart:update', this.onCart);
      }

      disconnectedCallback() {
        this._ready = false;
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('cart:update', this.onCart);
        document.documentElement.classList.remove('dlx-md-lock');
        this.panel?.remove();
        this.overlay?.remove();
      }

      open() {
        clearTimeout(this.hideTimer);
        this.lastFocus = document.activeElement;
        this.panel.hidden = false;
        this.overlay.hidden = false;
        this.showView('root', null, true);
        void this.panel.offsetWidth; // commit start state so the slide-in transition runs
        this.panel.classList.add('is-open');
        this.overlay.classList.add('is-open');
        this.burger?.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('dlx-md-lock');
        setTimeout(() => this.panel.querySelector('[data-dlx-close]')?.focus({ preventScroll: true }), 50);
      }

      close() {
        if (this.panel.hidden) return;
        this.panel.classList.remove('is-open');
        this.overlay.classList.remove('is-open');
        this.burger?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('dlx-md-lock');
        this.hideTimer = setTimeout(
          () => {
            this.panel.hidden = true;
            this.overlay.hidden = true;
          },
          reduce() ? 0 : 320
        );
        (this.lastFocus || this.burger)?.focus?.({ preventScroll: true });
      }

      showView(id, trigger, instant) {
        const views = this.panel.querySelectorAll('[data-view]');
        const toRoot = id === 'root';
        views.forEach((v) => {
          const on = v.dataset.view === id;
          v.hidden = !on;
          v.classList.toggle('is-current', on);
          v.classList.remove('is-entering', 'is-back');
          if (on && !instant && !reduce()) v.classList.add(toRoot ? 'is-back' : 'is-entering');
        });
        this.panel.querySelectorAll('[data-open-view]').forEach((b) =>
          b.setAttribute('aria-expanded', String(b.dataset.openView === id))
        );
        const scroller = this.panel.querySelector('.dlx-md__views');
        if (scroller) scroller.scrollTop = 0;
        if (instant) return;
        if (toRoot) {
          (this.lastView && this.panel.querySelector(`[data-open-view="${this.lastView}"]`))?.focus({ preventScroll: true });
        } else {
          this.lastView = id;
          this.panel.querySelector(`[data-view="${id}"] [data-back]`)?.focus({ preventScroll: true });
        }
      }

      trap(e) {
        const f = [...this.panel.querySelectorAll('a[href], button, input')].filter(
          (el) => !el.closest('[hidden]') && el.offsetParent !== null
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    customElements.define('dlx-menu-drawer', DlxMenuDrawer);
  }
})();
